import * as React from 'react';
import MediaQuery from 'react-responsive';
import Badge from '@telia-front/badge';
import Grid, { GridColumn } from '@telia-front/grid';
import GridCollapse, { GridCollapseProvider, GridCollapseTrigger } from '@telia-front/grid-collapse';
import { useIsMounted, useCurrentBreakPoint } from '@telia-front/helper';
import Icon from '@telia-front/icon';
import { useLabels } from '@telia-front/label-provider';
import { useTheme } from '@telia-front/theme-provider';
import ProgressCategory from '../progress-category/progress-category';
import ProgressFooter from '../progress-footer/progress-footer';
import ProgressItem from '../progress-item/progress-item';
const Progress = (props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const isMounted = useIsMounted();
    const { media } = useTheme();
    const currentBreak = useCurrentBreakPoint();
    const isMobile = currentBreak === 'xs' || currentBreak === 'sm';
    const labels = useLabels();
    const BEM = () => {
        const classArray = ['progress'];
        if (props.break && props.style !== 'card') {
            classArray.push(`has-break-${props.break}`);
        }
        if (props.style) {
            classArray.push(`progress--${props.style}`);
        }
        if (props.isBottom || !isMounted) {
            classArray.push('progress--bottom');
        }
        if (props.className) {
            classArray.push(props.className);
        }
        return classArray.join(' ');
    };
    const getBreakpoint = () => {
        const { break: breakPoint = 'xs' } = props;
        return media.width[breakPoint].min;
    };
    const getContent = () => {
        let content = null;
        React.Children.forEach(props.children, (child) => {
            if (typeof child === 'string' || typeof child === 'number' || child.type === ProgressCategory || child.type === ProgressFooter) {
                return;
            }
            if (child.props.state === 'current') {
                content = child.props.children;
            }
        });
        if (props.isBottom || !isMounted || props.style === 'card') {
            return React.createElement("div", { className: "progress__content" }, content);
        }
        if (isMounted && props.style !== 'card') {
            return React.createElement(MediaQuery, { minWidth: getBreakpoint() }, (matches) => setContent(matches, content));
        }
    };
    const setContent = (matches, content) => {
        if (matches) {
            return React.createElement("div", { className: "progress__content" }, content);
        }
        else {
            return null;
        }
    };
    const getValidChildren = () => {
        return React.Children.toArray(props.children).filter((child) => {
            return React.isValidElement(child) && child.props.type !== 'summary';
        });
    };
    const getNavItems = () => {
        let itemIndex = 0;
        return getValidChildren().map(child => {
            if (React.isValidElement(child) && child.type === ProgressItem) {
                itemIndex += 1;
                return React.cloneElement(child, {
                    break: props.break,
                    isBottom: props.isBottom || !isMounted,
                    style: props.style,
                    index: itemIndex,
                });
            }
            return child;
        });
    };
    const getProgressSteps = () => {
        if (!isMobile && props.style === 'card') {
            return (React.createElement("div", { className: "progress__card-nav" },
                React.createElement("div", { className: "progress__card-nav-container" },
                    React.createElement("ul", { className: "progress__steps" }, getNavItems()))));
        }
        return React.createElement("ul", { className: "progress__steps" }, getNavItems());
    };
    const indicatorItemBEM = (state) => {
        const classArray = ['progress__indicator-list-item'];
        if (state) {
            classArray.push(`is-${state}`);
        }
        return classArray.join(' ');
    };
    const progressDotBEM = (purpose, state) => {
        const classArray = ['progress__dot'];
        if (purpose) {
            classArray.push(`progress__dot--${purpose}`);
        }
        if (state) {
            classArray.push(`progress__dot--${state}`);
        }
        return classArray.join(' ');
    };
    const getProgressIndicatorArray = () => {
        const indicatorArray = [];
        getValidChildren()
            .filter(child => {
            return React.isValidElement(child) && child.type === ProgressItem;
        })
            .forEach((child, index) => {
            if (React.isValidElement(child)) {
                const { icon, purpose, state, subItems } = child.props;
                if (icon) {
                    indicatorArray.push({ icon, purpose, state });
                }
                else if (subItems) {
                    const subitemsArray = subItems.map((subItem, i) => {
                        return {
                            counter: `${index + 1}.${i + 1}`,
                            purpose: subItem.purpose,
                            state: subItem.state,
                        };
                    });
                    indicatorArray.push(...subitemsArray);
                }
                else {
                    indicatorArray.push({
                        counter: index + 1,
                        purpose,
                        state,
                    });
                }
            }
        });
        return indicatorArray;
    };
    const renderIndicatorDots = () => getProgressIndicatorArray().map((item, index) => {
        const getBadgeTextOrIcon = () => {
            if (item.icon) {
                return { icon: item.icon };
            }
            return { text: item.counter || '' };
        };
        return (React.createElement("span", { className: indicatorItemBEM(item.state), key: index },
            React.createElement("span", { className: "progress__indicator-step-wrapper" }, item.state === 'current' ? (React.createElement(Badge, Object.assign({ purpose: item.purpose === 'employee' ? 'employee-active' : 'selected', size: "large", width: "fixed" }, getBadgeTextOrIcon()))) : (React.createElement("span", { className: "progress__dot-background" },
                React.createElement("span", { className: progressDotBEM(item.purpose, item.state) }))))));
    });
    const indicatorIconBEM = () => {
        const classArray = ['progress__indicator-list-icon'];
        if (isOpen) {
            classArray.push('is-open');
        }
        return classArray.join(' ');
    };
    const getProgressStepsWithIndicatorDots = () => (React.createElement(GridCollapseProvider, { initialColumnCount: 0, isOpen: isOpen, onToggle: () => setIsOpen(!isOpen) },
        React.createElement(GridCollapseTrigger, null,
            React.createElement("button", { type: "button" },
                React.createElement("span", { className: "sr-only" }, isOpen ? labels.get('close') : labels.get('open')),
                React.createElement(Grid, { element: "span", noWrap: true, align: ['middle-xs'], gutter: "none" },
                    React.createElement(GridColumn, { width: ['max'] },
                        React.createElement("span", { className: "progress__indicator-list" }, renderIndicatorDots())),
                    React.createElement(GridColumn, { width: ['min'] },
                        React.createElement(Icon, { modifiers: "#arrow-down", className: indicatorIconBEM(), size: "sm" }))))),
        React.createElement(GridCollapse, { gutter: "none" }, getProgressSteps())));
    const getCardProgress = () => {
        return (React.createElement("div", { className: BEM() },
            isMobile ? getProgressStepsWithIndicatorDots() : getProgressSteps(),
            getContent()));
    };
    const getProgress = () => {
        return (React.createElement("div", { className: BEM() },
            getProgressSteps(),
            (props.break || props.isBottom) && getContent()));
    };
    return React.createElement(React.Fragment, null, props.style === 'card' ? getCardProgress() : getProgress());
};
export default Progress;
